import { StateService, TransitionService } from '@uirouter/angular';
import { IWizardButton, IWizardStep, GaWizard } from './ga-wizard';
import { Injectable, Type } from '@angular/core';
import { GaEventService } from '../services/ga-event.service';
import { GaMessageService } from '../message/ga-message.service';
import { Dictionary } from '../../../type_definitions/vendor';
import { BreadcrumbService } from '../breadcrumbs/breadcrumb.service';
import { TranslationService } from '../translation/translation.service';
import { GaValidateService } from '../formly/ga-validate.service';
import { IObject } from '../../../type_definitions/datatypes';

/**
 * @ngdoc service
 * @name ga2.common.service:wizardService
 *
 * @description The service that instantiate Wizard class and create actions/steps to be used in wizard
 */
@Injectable()
export class GaWizardService {
  private _instances:Dictionary<GaWizard> = {};

  constructor(
    private stateService:StateService,
    private breadcrumbService:BreadcrumbService,
    private gaEventService:GaEventService,
    private messageService:GaMessageService,
    private translationService:TranslationService,
    private validateService:GaValidateService,
    private transitionService:TransitionService
  ) {
    /**/
  }

  /**
   * @ngdoc method
   * @name ga2.common.service:wizardService#getInstance
   * @methodOf ga2.common.service:wizardService
   * @description Create new instance of WizardService class to isolate the properties
   * @return {class} WizardService
   *
   * @param {string} instanceId - reference name of service instance
   *
   */
  public getInstance(instanceId:string):GaWizard {
    if (!this._instances[ instanceId ]) {
      this._instances[ instanceId ] = new GaWizard(
        instanceId,
        this.breadcrumbService,
        this.stateService,
        this.gaEventService,
        this.messageService,
        this.translationService,
        this.validateService,
        this.transitionService
      );
    }
    // start instance if not yet started, register necessary listeners for wizard instance
    // or this should re-register state listener, since constructor will not be called again (case: when leaving wizard and re-enter again)
    this._instances[ instanceId ].startIfNecessary();

    return this._instances[ instanceId ];
  }

  /**
   * @ngdoc method
   * @name ga2.common.service:wizardService#createStep
   * @methodOf ga2.common.service:wizardService
   * @description Creates a new step for use in the wizard
   *
   * @param {string} id               unique id for step
   * @param {string} title            the title of the step (used in buttons, menu, ...)
   * @param {string=} component       the name of the component
   * @param {string} state            the state name that this step is associated with (e.g. 'ga2.main.order.location')
   * @param {IObject=} stateParams     the params for the step's state
   * @param {string[]=} stateChildren  a list of state names that should also be associated with this step
   * @param {Boolean=} enabled        when a step is disabled, it cannot be selected
   * @param {string=} reason          when disabled, a reason can be specified
   * @returns {Object} a step object
   */
  public createStep(id:string, title:string, component:Type<any>, state:string, stateParams?:IObject, stateChildren?:string[], enabled:boolean = true, reason?:string):IWizardStep {
    return <any>{
      id,
      title,
      state,
      stateParams,
      component,
      stateChildren,
      enabled,
      reason
    };
  }

  /**
   * @ngdoc method
   * @name ga2.common.service:wizardService#createActionButton
   * @methodOf ga2.common.service:wizardService
   * @description Creates a new action for use in the wizard
   *
   * @param {string} id               unique id for action
   * @param {string} label            label of action button
   * @param {string} classes          css classes will be used in action button
   * @param {Function=} click         onClick action function
   * @param {Boolean=} hidden         hidden status of action button
   * @param {Boolean=} preventDisable prevent disability status
   * @param {string=} iconClasses     icon classes for action button
   * @param {boolean=} iconVisible    visibility of action button
   * @param {IWizardButton[]} buttons dropdown action buttons
   * @returns {IWizardButton} an action object
   */
  public createActionButton(id:string, label:string, classes:string | string[], click?:Function, hidden?:boolean, preventDisable?:boolean, iconClasses?:string, iconVisible?:boolean, buttons?:IWizardButton[]):IWizardButton {
    return {
      id,
      label,
      classes,
      click,
      hidden,
      preventDisable,
      iconClasses,
      iconVisible,
      buttons
    };
  }
}